import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../../Common/isRequestCanceled";
import {
    REPORTS_BASE_URL,
    HOURLY_SALES_REPORT_API,
    HOURLY_SALES_SAVE_SETTINGS_API,
    HOURLY_SALES_LOAD_SETTINGS_API,
} from "../../../../Constants/Config";

const DEFAULT_SETTINGS = { startHour: 0, endHour: 23 };

const initialState = {
    loading: false,
    hoursData: {},
    hourlyList: [],
    totals: {
        total_sales_count: 0,
        total_net_sales: 0,
        total_cost_price: 0,
        total_gross_profit: 0,
        total_margin: 0,
        total_tax: 0,
    },
    settings: { ...DEFAULT_SETTINGS },
    settingsLoaded: false,
    settingsLoading: false,
    settingsSaving: false,
    comparison: null,
    status: false,
    successMessage: "",
    error: "",
};

export const fetchHourlySalesReport = createAsyncThunk(
    "HourlySalesReport/fetchHourlySalesReport",
    async (data, { rejectWithValue }) => {
        try {
            const { token, ...dataNew } = data;

            const response = await axios.post(
                REPORTS_BASE_URL + HOURLY_SALES_REPORT_API,
                dataNew,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data.status === true) {
                return {
                    hoursData: response.data.result.hoursData || {},
                    hourlyList: response.data.result.hourly_list || [],
                    totals: response.data.result.totals || {
                        total_sales_count: 0,
                        total_net_sales: 0,
                        total_cost_price: 0,
                        total_gross_profit: 0,
                        total_margin: 0,
                        total_tax: 0,
                    },
                    comparison: response.data.result.comparison || null,
                    status: response.data.status
                };
            }

            return rejectWithValue({
                message: response.data.message || "Failed to fetch data",
                status: response.status,
                data: response.data
            });
        } catch (error) {
            const customError = {
                message: toThunkRejectValue(error).message,
                status: toThunkRejectValue(error).status,
                data: error.response ? error.response.data : null,
            };
            return rejectWithValue(customError);
        }
    }
);

export const loadHourlySalesSettings = createAsyncThunk(
    "HourlySalesReport/loadSettings",
    async ({ merchant_id, token, userTypeData = {} }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${REPORTS_BASE_URL}${HOURLY_SALES_LOAD_SETTINGS_API}`,
                { merchant_id, ...userTypeData },
                {
                    params: { merchant_id },
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const cfg = response.data?.data?.settingsConfig;
            if (response.data?.status && cfg) {
                return {
                    startHour: Number.isInteger(cfg.startHour) ? cfg.startHour : DEFAULT_SETTINGS.startHour,
                    endHour: Number.isInteger(cfg.endHour) ? cfg.endHour : DEFAULT_SETTINGS.endHour,
                };
            }
            return { ...DEFAULT_SETTINGS };
        } catch (error) {
            return rejectWithValue({
                message: toThunkRejectValue(error).message,
                status: toThunkRejectValue(error).status,
            });
        }
    }
);

export const saveHourlySalesSettings = createAsyncThunk(
    "HourlySalesReport/saveSettings",
    async ({ merchant_id, token, userTypeData = {}, settings }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${REPORTS_BASE_URL}${HOURLY_SALES_SAVE_SETTINGS_API}`,
                { merchant_id, ...userTypeData, settingsConfig: settings },
                {
                    params: { merchant_id },
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            if (response.data?.status) {
                const cfg = response.data?.data?.settingsConfig || settings;
                return {
                    startHour: Number.isInteger(cfg.startHour) ? cfg.startHour : settings.startHour,
                    endHour: Number.isInteger(cfg.endHour) ? cfg.endHour : settings.endHour,
                };
            }
            return rejectWithValue({
                message: response.data?.message || "Failed to save settings",
            });
        } catch (error) {
            return rejectWithValue({
                message: toThunkRejectValue(error).message,
                status: toThunkRejectValue(error).status,
            });
        }
    }
);

const HourlySalesReportSlice = createSlice({
    name: "HourlySalesReport",
    initialState,
    reducers: {
        clearHourlySalesData: (state) => {
            state.hoursData = {};
            state.hourlyList = [];
            state.comparison = null;
            state.totals = {
                total_sales_count: 0,
                total_net_sales: 0,
                total_cost_price: 0,
                total_gross_profit: 0,
                total_margin: 0,
                total_tax: 0,
            };
            state.status = false;
            state.loading = false;
            state.error = "";
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchHourlySalesReport.pending, (state) => {
            state.loading = true;
            state.error = "";
        });
        builder.addCase(fetchHourlySalesReport.fulfilled, (state, action) => {
            state.loading = false;
            state.hoursData = action.payload.hoursData || {};
            state.hourlyList = action.payload.hourlyList || [];
            state.totals = action.payload.totals || {
                total_sales_count: 0,
                total_net_sales: 0,
                total_cost_price: 0,
                total_gross_profit: 0,
                total_margin: 0,
                total_tax: 0,
            };
            state.comparison = action.payload.comparison || null;
            state.status = action.payload.status || false;
            state.error = "";
        });
        builder.addCase(fetchHourlySalesReport.rejected, (state, action) => {
            state.loading = false;
            state.hoursData = {};
            state.hourlyList = [];
            state.comparison = null;
            state.totals = {
                total_sales_count: 0,
                total_net_sales: 0,
                total_cost_price: 0,
                total_gross_profit: 0,
                total_margin: 0,
                total_tax: 0,
            };
            state.status = false;
            state.error = action.payload?.message || action.error?.message || "";
        });

        builder.addCase(loadHourlySalesSettings.pending, (state) => {
            state.settingsLoading = true;
        });
        builder.addCase(loadHourlySalesSettings.fulfilled, (state, action) => {
            state.settings = action.payload;
            state.settingsLoaded = true;
            state.settingsLoading = false;
        });
        builder.addCase(loadHourlySalesSettings.rejected, (state) => {
            state.settings = { ...DEFAULT_SETTINGS };
            state.settingsLoaded = true;
            state.settingsLoading = false;
        });

        builder.addCase(saveHourlySalesSettings.pending, (state) => {
            state.settingsSaving = true;
        });
        builder.addCase(saveHourlySalesSettings.fulfilled, (state, action) => {
            state.settings = action.payload;
            state.settingsSaving = false;
        });
        builder.addCase(saveHourlySalesSettings.rejected, (state) => {
            state.settingsSaving = false;
        });
    },
});

export const { clearHourlySalesData } = HourlySalesReportSlice.actions;
export default HourlySalesReportSlice.reducer;
